import React, { useState, useEffect } from "react";
import { Table, Button, Form } from "react-bootstrap";
import { FaArrowRight, FaArrowLeft } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { LuPlus } from "react-icons/lu";
import { CiSearch } from "react-icons/ci";
import { IoClose } from "react-icons/io5";
import * as apicall from "../../components/Apicall";
import Skeleton from "../../components/Loader/Skeleton";

const Vendors = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Combined useEffect for search and data fetching
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchVendors();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, page, limit, selectedFilter]);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      let url = `get_vendor_details?page=${page}&limit=${limit}`;

      // Add filters to URL if they exist
      if (selectedFilter && selectedFilter !== "All") {
        url += `&status=${selectedFilter === "Active" ? "1" : "0"}`;
      }
      if (searchQuery && searchQuery.trim() !== "") {
        url += `&name=${encodeURIComponent(searchQuery.trim())}`;
      }

      const response = await apicall.get(url);
      if (response && response.data) {
        setVendors(
          response.data.map((vendor) => ({
            id: vendor.vendor_id,
            user_id: vendor.user_id,
            name:
              vendor.first_name +
              (vendor.last_name ? ` ${vendor.last_name}` : ""),
            serviceType: vendor.service_name || "",
            totalUsers: vendor.user_count ?? 0,
            plans: vendor.plan_name || "",
            status: vendor.status || "",
            daysLeft: vendor.subscription_expire_in_days ?? 0,
          }))
        );
        setTotalCount(response.pagination?.total || 0);
      } else {
        console.error("Invalid response format:", response);
        setVendors([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch vendor data:", error);
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredVendors = vendors.filter(
    (vendor) =>
      (selectedFilter === "All" ||
        (vendor.status &&
          vendor.status.trim().toLowerCase() ===
            selectedFilter.trim().toLowerCase())) &&
      Object.values(vendor).some((value) =>
        value?.toString().toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const renderStatus = (status) => {
    const normalized = (status || "").toLowerCase();
    const isActive = normalized === "active";
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 12px",
          fontSize: "14px",
          borderRadius: "16px",
          backgroundColor: isActive ? "#ECFBEE" : "#F9F9F9",
          border: `1px solid ${isActive ? "#C4EBCD" : "#E9E9E9"}`,
          color: "#000000",
          fontWeight: "500",
        }}
      >
        {status}
      </span>
    );
  };

  const renderDaysLeft = (days) => {
    if (days === null) return "";
    return (
      <span className="primaryColorText ms-2" style={{ fontSize: "14px" }}>
        ({days} days left)
      </span>
    );
  };

  const handleViewProfile = (id) => navigate(`/vendors/view-profile/${id}`);
  const handleAddVendor = () => navigate("/vendors/add");
  const handleEditVendor = (id) => navigate(`/vendors/edit/${id}`);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      fetchVendors(); // Immediate search on Enter
    }
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setSelectedFilter(value);
    setPage(1); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedFilter("All");
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };

  // Pagination helpers
  const totalPages = Math.ceil(totalCount / limit);
  const getPageNumbers = () => {
    const pageNumbers = [];
    const totalPagesToShow = 5;
    const halfWay = Math.floor(totalPagesToShow / 2);

    if (totalPages <= totalPagesToShow) {
      // If total pages are less than or equal to totalPagesToShow, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);

      let startPage = Math.max(2, page - halfWay);
      let endPage = Math.min(totalPages - 1, page + halfWay);

      // Adjust start and end if we're near the beginning or end
      if (page <= halfWay + 1) {
        endPage = totalPagesToShow - 1;
      } else if (page >= totalPages - halfWay) {
        startPage = totalPages - totalPagesToShow + 2;
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pageNumbers.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }

      // Always show last page
      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  return (
    <div className="px-4 py-3">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="m-0 fw-bold">Vendors</h5>
        <Button
          variant="danger"
          size="sm"
          onClick={handleAddVendor}
          className="custom-btn"
          style={{ fontSize: "13px" }}
        >
          <LuPlus size={18} /> Add Vendor
        </Button>
      </div>

      {/* Search & Filter */}
      <div className="row mb-4">
        <div className="col-md-4">
          {loading ? (
            <Skeleton width="100%" height={40} />
          ) : (
            <div className="position-relative">
              <CiSearch className="position-absolute top-50 start-0 translate-middle-y ms-3" />
              <input
                type="text"
                className="form-control custom-input-search"
                placeholder="Search for vendor"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                autoFocus
                style={{
                  paddingRight: searchQuery ? "36px" : "12px",
                }}
              />
              {searchQuery && (
                <IoClose
                  className="position-absolute top-50 end-0 translate-middle-y me-3 text-secondary"
                  style={{ cursor: "pointer", fontSize: "20px" }}
                  onClick={handleClearSearch}
                />
              )}
            </div>
          )}
        </div>

        <div className="col d-flex gap-2">
          {loading ? (
            <>
              <Skeleton width={60} height={24} />
              <Skeleton width={140} height={40} />
            </>
          ) : (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  fontSize: "14px",
                  fontWeight: "400",
                  color: "#000000",
                }}
              >
                Filter by:
              </div>
              <Form.Select
                value={selectedFilter}
                onChange={handleFilterChange}
                className="custom-select  width-small"
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">De-Active</option>
              </Form.Select>
              {(selectedFilter !== "All" || searchQuery) && (
                <button
                  className="btn btn-link"
                  onClick={handleClearFilters}
                  style={{
                    height: "40px",
                    fontSize: "14px",
                    color: "#DC3545",
                    textDecoration: "none",
                  }}
                >
                  Clear Filters
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Vendor Table */}
      <div className="bg-white rounded-2">
        <div className="table-responsive">
          <Table
            bordered
            hover
            className="align-middle mb-0 bg-custom-light border-custom custom-table"
            style={{ fontSize: "14px" }}
          >
            <thead>
              <tr className="table-header-row">
                <th>Sl. No</th>
                <th>Vendor Name</th>
                <th>Service Type</th>
                <th>Total Users</th>
                <th>Plans</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array(5)
                  .fill(null)
                  .map((_, index) => (
                    <tr key={index}>
                      <td>
                        <Skeleton width="30px" />
                      </td>
                      <td>
                        <Skeleton width="150px" />
                      </td>
                      <td>
                        <Skeleton width="120px" />
                      </td>
                      <td>
                        <Skeleton width="80px" />
                      </td>
                      <td>
                        <Skeleton width="100px" />
                      </td>
                      <td>
                        <Skeleton width="80px" />
                      </td>
                      <td>
                        <div className="d-flex gap-3">
                          <Skeleton width="80px" />
                          <Skeleton width="80px" />
                        </div>
                      </td>
                    </tr>
                  ))
              ) : filteredVendors.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    No vendor found
                  </td>
                </tr>
              ) : (
                filteredVendors.map((vendor, index) => (
                  <tr key={vendor.id}>
                    <td>{(page - 1) * limit + index + 1}</td>
                    <td>{vendor.name}</td>
                    <td className="secondary-text">{vendor.serviceType}</td>
                    <td className="secondary-text">{vendor.totalUsers}</td>
                    <td className="secondary-text">
                      {vendor.plans}{" "}
                      {vendor.daysLeft > 0 ? (
                        <span style={{ color: "#C60F0F" }}>
                          ({vendor.daysLeft} days left)
                        </span>
                      ) : (
                        ""
                      )}
                    </td>
                    <td>{renderStatus(vendor.status)}</td>
                    <td>
                      <div className="d-flex gap-3">
                        <Button
                          variant="link"
                          className="text-decoration"
                          style={{ color: "#1E2EBE", fontSize: "14px" }}
                          // onClick={() => handleEditVendor(vendor.id)}
                          onClick={() => handleEditVendor(vendor.user_id)}
                        >
                          Edit Profile
                        </Button>
                        <Button
                          variant="link"
                          className="p-0 text-decoration"
                          style={{
                            color:
                              vendor.status &&
                              vendor.status.toLowerCase() !== "active"
                                ? "#aaa"
                                : "#C9194A",
                            fontSize: "14px",
                            cursor:
                              vendor.status &&
                              vendor.status.toLowerCase() !== "active"
                                ? "not-allowed"
                                : "pointer",
                            pointerEvents:
                              vendor.status &&
                              vendor.status.toLowerCase() !== "active"
                                ? "none"
                                : "auto",
                            opacity:
                              vendor.status &&
                              vendor.status.toLowerCase() !== "active"
                                ? 0.5
                                : 1,
                          }}
                          onClick={() => handleViewProfile(vendor.id)}
                          disabled={
                            vendor.status &&
                            vendor.status.toLowerCase() !== "active"
                          }
                        >
                          View Profile
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-end align-items-center mt-4">
        <div className="d-flex align-items-center gap-2">
          <button
            className="btn bg-white text-black btn-outline-borders d-flex align-items-center px-2"
            style={{ height: "32px", fontSize: "14px", minWidth: "32px" }}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
          >
            <FaArrowLeft /> Prev
          </button>

          <div className="d-flex">
            {getPageNumbers().map((num, index) => (
              <button
                key={index}
                className={`btn ${
                  num === "..."
                    ? "btn-outline-borders bg-white text-black"
                    : page === num
                    ? "bg-black text-white"
                    : "btn-outline-borders bg-white text-black"
                }`}
                style={{
                  height: "32px",
                  fontSize: "14px",
                  marginRight:
                    index !== getPageNumbers().length - 1 ? "6px" : "0",
                  borderRadius: "4px",
                  padding: "0 12px",
                  fontWeight: page === num ? 600 : 400,
                  border:
                    page === num ? "1px solid black" : "1px solid #E3EBF8",
                  minWidth: "32px",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: num === "..." ? "default" : "pointer",
                  pointerEvents: num === "..." ? "none" : "auto",
                }}
                onClick={() => num !== "..." && setPage(num)}
                disabled={num === "..."}
              >
                {num}
              </button>
            ))}
          </div>

          <button
            className="btn bg-white text-black btn-outline-borders d-flex align-items-center px-2"
            style={{ height: "32px", fontSize: "14px", minWidth: "32px" }}
            onClick={() => setPage((prev) => prev + 1)}
            disabled={page === totalPages}
          >
            <FaArrowRight /> Next
          </button>

          <select
            className="form-select ms-2 bg-white text-black btn-outline-borders"
            style={{
              width: "140px",
              height: "34px",
              fontSize: "14px",
              borderRadius: "6px",
            }}
            value={limit}
            onChange={(e) => {
              setPage(1);
              setLimit(Number(e.target.value));
            }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default Vendors;
